import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { NumericFormat } from 'react-number-format';
import MainCard from 'components/MainCard';
import { withValueCap } from 'pages/globals/global';
import dayjs from 'dayjs';
import { constantFormValues } from '../../root';
import { utcConverter } from '../LeaseUtils/helper';

const IFRS16FieldsModifications = ({ screen, rowIndex, initialChangeLength = 0 }) => {
	const form = Form.useFormInstance();
	const handleInputMonthChange = (e, index) => {
		const effectiveDate = dayjs(e);
		const leaseStart = dayjs(form.getFieldValue('leaseStartDate'));

		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());

		monthDifference += 1;

		if (monthDifference < 0) {
			monthDifference = 0;
		}

		// Get the current changes list
		const currentChanges = form.getFieldValue('changes') || [];

		// Only update this specific row, don't modify the entire form
		// This prevents triggering a full form change detection
		if (currentChanges[index]) {
			// Create a new array with the updated values to avoid direct mutation
			const updatedChanges = [...currentChanges];
			updatedChanges[index] = {
				...updatedChanges[index],
				inputChangeMonth: monthDifference,
				inputChangeEffectiveDate: utcConverter(effectiveDate),
			};

			// Update the form in a single operation
			form.setFieldsValue({ changes: updatedChanges });
		}
	};
	// Function to determine the dynamic date range for each row
	const getMaxDateForRow = (index) => {
		if (index === 0) {
			// First row's effective date is always the lease start date and is disabled
			return null;
		}

		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));
		if (!leaseStartDate) return null;

		const changes = form.getFieldValue('changes');
		if (!changes || !changes[index - 1]) return null;

		// Get previous row's lease term
		const prevRowLeaseTerm = changes[index - 1].inputChangeLeaseTerm;
		if (!prevRowLeaseTerm) return null;

		// Calculate max date: lease start date + previous row's lease term (in months)
		return leaseStartDate.add(parseInt(prevRowLeaseTerm), 'month').subtract(1, 'day');
	};

	return (
		<Row gutter={[16, 0]}>
			<Col xs={24}>
				<Form.List name="changes">
					{(fields) => {
						return (
							<>
								<MainCard content={false} sx={{ mb: 2, mt: '12px', borderColor: '#d9d9d9' }}>
									<TableContainer
										sx={{
											width: '100%',
											overflowX: 'auto',
											position: 'relative',
											display: 'block',
											'& td, & th': { whiteSpace: 'nowrap' },
											'& td': {
												borderTop: '0px',
												borderLeft: '0px',
												borderBottom: '0px',
											},
											'& td:not(:last-child)': {
												borderRight: '1px solid #e0e0e0',
											},
											'& tr:not(:last-child)': {
												borderBottom: '1px solid #e0e0e0',
											},
											'& th': {
												borderBottom: '1px solid #e0e0e0',
											},
										}}
									>
										<Table
											sx={{
												'& .MuiTableBody-root .MuiTableCell-root': {
													px: 0.5,
													py: 0.5,
												},
												tableLayout: 'fixed',
											}}
										>
											<TableHead>
												<TableRow>
													{constantFormValues?.changes?.orderedRowHeader.map((key) => (
														<TableCell
															key={constantFormValues?.changes?.changeList[key].key}
															sx={{ fontSize: '14px', backgroundColor: '#e1f0ff !important' }}
															align="center"
														>
															{constantFormValues?.changes?.changeList[key].label}
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{fields?.map((field, index) => {
													const maxDate = getMaxDateForRow(index);
													return (
														<TableRow key={index} style={{ height: '60px' }}>
															<TableCell style={{ position: 'relative' }}>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
																	]}
																	rules={constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].rules}
																>
																	<DatePicker
																		disabled={
																			index === 0
																				? true
																				: screen === 'REVISE'
																					? true
																					: screen === 'VIEW'
																						? true
																						: screen === 'EDIT'
																							? index < initialChangeLength
																								? true
																								: false
																							: false
																		}
																		onChange={(date) => handleInputMonthChange(date, index)}
																		format="YYYY-MM-DD"
																		style={{
																			width: '100%',
																			height: '32px',
																		}}
																		className="antd-datePicker"
																		disabledDate={(current) => {
																			if (!current) return false;

																			const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));
																			// Check if before lease start date
																			const isBefore = current.isBefore(leaseStartDate, 'day');

																			// Check if after max allowed date (if applicable)
																			let isAfter = false;
																			if (maxDate) {
																				isAfter = current.isAfter(maxDate, 'day');
																			}

																			return isBefore || isAfter;
																		}}
																	/>
																</Form.Item>
															</TableCell>
															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeDescription'].key,
																	]}
																	// initialValue={
																	// 	constantFormValues?.changes?.changeList['inputChangeDescription'].options[0]
																	// 		.value
																	// }
																>
																	<Select
																		disabled
																		showSearch
																		style={{
																			textAlign: 'center',
																		}}
																		filterOption={true}
																		options={
																			constantFormValues?.changes?.changeList['inputChangeDescription'].options
																		}
																	>
																		{constantFormValues?.changes?.changeList[
																			'inputChangeDescription'
																		].options?.map(({ key, value, label }) => (
																			<Select.Option key={key} value={value}>
																				{label}
																			</Select.Option>
																		))}
																	</Select>
																</Form.Item>
															</TableCell>
															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key,
																	]}
																>
																	<Input
																		disabled
																		type="number"
																		style={{
																			marginBottom: 0,
																			textAlign: 'center',
																			color:
																				index !== 0
																					? form.getFieldValue([
																							'changes',
																							index,
																							'inputChangeLeaseTerm',
																						]) ===
																						form.getFieldValue([
																							'changes',
																							index - 1,
																							'inputChangeLeaseTerm',
																						])
																						? 'black'
																						: 'blue'
																					: 'black',
																		}}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell align="center">
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['inputChangeDiscountRate'].key,
																	]}
																>
																	<NumericFormat
																		size="large"
																		decimalScale={2}
																		placeholder="0.00"
																		fixedDecimalScale
																		thousandSeparator=","
																		isAllowed={withValueCap}
																		style={{
																			textAlign: 'center',
																			height: '32px',
																			borderRadius: '4px',
																			fontSize: '13px',
																			color:
																				index !== 0
																					? form.getFieldValue([
																							'changes',
																							index,
																							'inputChangeDiscountRate',
																						]) ===
																						form.getFieldValue([
																							'changes',
																							index - 1,
																							'inputChangeDiscountRate',
																						])
																						? 'black'
																						: 'blue'
																					: 'black',
																		}}
																		customInput={Input}
																		disabled={
																			screen === 'CREATE' || screen === 'VIEW'
																				? true
																				: screen !== 'CREATE'
																					? index < rowIndex
																						? true
																						: false
																					: false
																		}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[
																		field.name,
																		constantFormValues?.changes?.changeList['paymentFrequency'].key,
																	]}
																>
																	<Select
																		disabled
																		showSearch
																		style={{
																			textAlign: 'center',
																		}}
																		filterOption={true}
																		options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
																		defaultValue={
																			constantFormValues?.changes?.changeList['paymentFrequency'].options[0]
																				.value
																		}
																	>
																		{constantFormValues?.changes?.changeList['paymentFrequency'].options?.map(
																			({ key, value, label }) => (
																				<Select.Option key={key} value={value}>
																					{label}
																				</Select.Option>
																			)
																		)}
																	</Select>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
																>
																	<Select
																		disabled
																		showSearch
																		style={{
																			textAlign: 'center',
																		}}
																		filterOption={true}
																		options={constantFormValues?.changes?.changeList['paymentTime'].options}
																		defaultValue={
																			constantFormValues?.changes?.changeList['paymentTime'].options[0].value
																		}
																	>
																		{constantFormValues?.changes?.changeList['paymentTime'].options?.map(
																			({ key, value, label }) => (
																				<Select.Option key={key} value={value}>
																					{label}
																				</Select.Option>
																			)
																		)}
																	</Select>
																</Form.Item>
															</TableCell>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</MainCard>
							</>
						);
					}}
				</Form.List>
			</Col>
		</Row>
	);
};

export default IFRS16FieldsModifications;
